import React from 'react';
import { UnilevelLegsLogicReturn, OrganizationChartNodeProps, OrganizationChartNodeData } from '../UnilevelLegs.types';
import '../UnilevelLegs.css';

interface BasicUnilevelLegsSkinProps extends UnilevelLegsLogicReturn {
  // 스킨 옵션
  title?: string;
  emptyText?: string;
  loadingText?: string;
  errorText?: string;
  loginRequiredText?: string;
  showNavigationButtons?: boolean;
  showBreadcrumb?: boolean;
  showDebugMode?: boolean;
  showZoomControls?: boolean;
  showNodePV?: boolean;
  showNodeRank?: boolean;
  showNodeGrade?: boolean;
  showNodePhone?: boolean;
  showNodeCenter?: boolean;
  showNodeGroupPV?: boolean;
  showMoreButton?: boolean;
  lineColor?: string;
  nodeBackgroundColor?: string;
  nodeBorderColor?: string;
  nodeShadow?: string;
  titleBackgroundColor?: string;
  titleTextColor?: string;
  contentTextColor?: string;
  bubbleBadgeColor?: string;
  bubbleBadgeTextColor?: string;
  buttonPrimaryColor?: string;
  buttonSecondaryColor?: string;
  buttonSuccessColor?: string;
  buttonDangerColor?: string;
  containerBackgroundColor?: string;
  breadcrumbColor?: string;
  breadcrumbActiveColor?: string;
}

/**
 * 조직도 노드 컴포넌트 - 유니레벨 구조
 */
const OrganizationChartNode: React.FC<OrganizationChartNodeProps & {
  showNodePV?: boolean;
  showNodeRank?: boolean;
  showNodeGrade?: boolean;
  showNodePhone?: boolean;
  showNodeCenter?: boolean;
  showNodeGroupPV?: boolean;
  showMoreButton?: boolean;
  bubbleBadgeColor?: string;
  bubbleBadgeTextColor?: string;
}> = ({ 
  node, 
  onClick, 
  onMoreClick,
  loadingNodes = new Set(),
  isRootNode = false, 
  lineColor = '#3f51b5',
  showNodePV = true,
  showNodeRank = true,
  showNodeGrade = true,
  showNodePhone = true,
  showNodeCenter = true,
  showNodeGroupPV = true,
  showMoreButton = true,
  bubbleBadgeColor = '#2196f3',
  bubbleBadgeTextColor = '#ffffff'
}) => {
  if (!node) return null;
  
  // 자식 노드 확인 - 유니레벨은 여러 자식 가능
  const hasChildren = node.children && node.children.length > 0;
  const childrenCount = node.children ? node.children.length : 0;
  
  // node.class에 따라 타이틀 클래스 선택
  const titleClassName = `title ${node.class && node.class.includes('shipper') ? 'shipper-title' : 'normal-title'}`;
  
  // CSS 변수로 선 색상 전달
  const nodeStyle = {
    '--line-color': lineColor
  } as React.CSSProperties;
  
  return (
    <li className={`org-node ${hasChildren ? 'has-children' : ''}`} style={nodeStyle}>
      <div className="node-content">
        <div 
          className={`node ${node.class || ''} ${isRootNode ? 'root-node' : ''}`} 
          onClick={() => onClick(node, isRootNode)}
        >
          <div className={titleClassName}>
            {node.name || node.userId || ''}
          </div>
          <div 
            className="content" 
            style={node.backgroundColor ? { backgroundColor: node.backgroundColor } : undefined}
          >
            {/* 동그라미 풍선 (totalPV, rank, grade) */}
            {(() => {
              const bubbleItems = [];
              const pvValue = node.totalPV !== undefined && node.totalPV !== null ? 
                node.totalPV : node.pv;
              if (showNodePV && pvValue !== undefined && pvValue !== null) {
                const formattedPV = pvValue.toLocaleString();
                bubbleItems.push(`PV ${formattedPV}`);
              }
              if (showNodeRank && node.rank) {
                bubbleItems.push(node.rank);
              }
              if (showNodeGrade && node.grade) {
                bubbleItems.push(node.grade);
              }
              
              if (bubbleItems.length > 0) {
                return (
                  <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                    {bubbleItems.map((item, index) => (
                      <span 
                        key={index} 
                        className="bubble-badge"
                        style={{
                          backgroundColor: bubbleBadgeColor,
                          color: bubbleBadgeTextColor
                        }}
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                );
              }
              return null;
            })()}
            
            {/* 텍스트 정보 - API에서 받은 데이터만 표시 */}
            {(() => {
              const textInfo = [];
              
              // userId 표시
              if (node.userId) {
                textInfo.push(`ID: ${node.userId}`);
              }
              
              // name 표시 (userId와 다른 경우에만)
              if (node.name && node.name !== node.userId) {
                textInfo.push(`이름: ${node.name}`);
              }
              
              // phone 표시
              if (showNodePhone && node.phone && node.phone.trim() !== '') {
                textInfo.push(`전화: ${node.phone}`);
              }
              
              // center 표시
              if (showNodeCenter && node.center) {
                textInfo.push(`센터: ${node.center}`);
              }
              
              // groupPv 표시
              if (showNodeGroupPV && node.groupPv !== undefined && node.groupPv !== null && node.groupPv > 0) {
                const formattedGroupPV = node.groupPv.toLocaleString();
                textInfo.push(`그룹PV: ${formattedGroupPV}`);
              }
              
              if (textInfo.length > 0) {
                return (
                  <div style={{ textAlign: 'left', fontSize: '11px', lineHeight: 1.4 }}>
                    {textInfo.map((info, index) => (
                      <div key={index}>{info}</div>
                    ))}
                  </div>
                );
              }
              return null;
            })()}
          </div>
        </div>
        
        {/* 플러스 버튼: underMore가 true인 경우에만 표시 */}
        {showMoreButton && node.underMore && (
          <div className="more-button-container">
            <button 
              className="more-button" 
              onClick={(e) => {
                e.stopPropagation();
                if (onMoreClick) {
                  onMoreClick(node.id);
                }
              }}
              disabled={loadingNodes.has(node.id)}
            >
              {loadingNodes.has(node.id) ? '...' : '+'}
            </button>
          </div>
        )}
      </div>
      
      {/* 자식 노드들 */}
      {hasChildren && (
        <div className="children">
          <ul className={childrenCount === 1 ? 'single-child' : 'multiple-children'}>
            {node.children?.map((child, index) => (
              <OrganizationChartNode 
                key={child.id || index}
                node={child} 
                onClick={onClick} 
                onMoreClick={onMoreClick}
                loadingNodes={loadingNodes}
                isRootNode={false}
                lineColor={lineColor}
                showNodePV={showNodePV}
                showNodeRank={showNodeRank}
                showNodeGrade={showNodeGrade}
                showNodePhone={showNodePhone}
                showNodeCenter={showNodeCenter}
                showNodeGroupPV={showNodeGroupPV}
                showMoreButton={showMoreButton}
                bubbleBadgeColor={bubbleBadgeColor}
                bubbleBadgeTextColor={bubbleBadgeTextColor}
              />
            ))}
          </ul>
        </div>
      )}
    </li>
  );
};

const BasicUnilevelLegsSkin: React.FC<BasicUnilevelLegsSkinProps> = ({
  // Logic props
  currentUserId,
  debugMode,
  mockingEnabled,
  customApiUrl,
  nodeHistory,
  zoomLevel,
  offset,
  isDragging,
  loadingNodes,
  user,
  unilevelTreeData,
  unilevelTreeLoading,
  unilevelTreeError,
  primaryColor,
  secondaryColor,
  containerRef,
  handleNodeClick,
  handleMoreButtonClick,
  goToPreviousNode,
  goToRootNode,
  toggleDebugMode,
  enableMockData,
  retryWithRealApi,
  handleWheel,
  handleMouseDown,
  handleMouseMove,
  handleMouseUp,
  handleTouchStart,
  handleTouchMove,
  handleTouchEnd,
  resetZoom,
  setMockingEnabled,
  setCustomApiUrl,
  t,
  generateMockData,
  // Skin options
  title = '추천 레그',
  emptyText = '데이터가 없습니다',
  loadingText = '조직도를 불러오는 중입니다...',
  errorText = '데이터를 불러오는데 실패했습니다',
  loginRequiredText = '로그인이 필요한 서비스입니다.',
  showNavigationButtons = true,
  showBreadcrumb = true,
  showDebugMode = false,
  showZoomControls = true,
  showNodePV = true,
  showNodeRank = true,
  showNodeGrade = true,
  showNodePhone = true,
  showNodeCenter = true,
  showNodeGroupPV = true,
  showMoreButton = true,
  lineColor = '#3f51b5',
  nodeBackgroundColor = '#ffffff',
  nodeBorderColor = '#e0e0e0',
  nodeShadow = '0 2px 4px rgba(0,0,0,0.1)',
  titleBackgroundColor = '#3f51b5',
  titleTextColor = '#ffffff',
  contentTextColor = '#333333',
  bubbleBadgeColor = '#2196f3',
  bubbleBadgeTextColor = '#ffffff',
  buttonPrimaryColor = '#3f51b5',
  buttonSecondaryColor = '#6c757d',
  buttonSuccessColor = '#28a745',
  buttonDangerColor = '#dc3545',
  containerBackgroundColor = '#ffffff',
  breadcrumbColor = '#666666',
  breadcrumbActiveColor = '#3f51b5'
}) => {
  // 버튼 스타일 설정
  const buttonStyle = {
    backgroundColor: buttonPrimaryColor || primaryColor,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '5px 15px',
    marginRight: '10px',
    marginBottom: '10px',
    cursor: 'pointer',
    fontSize: '14px'
  };
  
  // 비활성화된 버튼 스타일
  const disabledButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#ccc',
    cursor: 'not-allowed',
    opacity: 0.7
  };

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <h2 className="mb-4 d-flex">
        {t(title)}
        {showDebugMode && (
          <button 
            onClick={toggleDebugMode} 
            style={{marginLeft: 'auto', background: 'none', border: 'none', fontSize: '0.8rem', color: '#666'}}
            title={t("디버그 모드 전환")}
          >
            🛠️
          </button>
        )}
      </h2>
      
      {/* 네비게이션 버튼 */}
      {showNavigationButtons && (
        <div className="unilevel-navigation-controls">
          <button 
            style={currentUserId && nodeHistory.length > 0 ? buttonStyle : disabledButtonStyle}
            onClick={goToPreviousNode}
            disabled={!currentUserId || nodeHistory.length === 0}
            title={t("이전으로 돌아가기")}
          >
            {t("이전으로")}
          </button>
          <button 
            style={currentUserId && String(currentUserId) !== String(user?.id) ? buttonStyle : disabledButtonStyle}
            onClick={goToRootNode}
            disabled={!currentUserId || String(currentUserId) === String(user?.id)}
            title={t("처음으로 돌아가기")}
          >
            {t("처음으로")}
          </button>
          {showZoomControls && (
            <button 
              style={{...buttonStyle, backgroundColor: buttonSecondaryColor}}
              onClick={resetZoom}
              title={t("줌 초기화")}
            >
              {t("줌 초기화")}
            </button>
          )}
          {debugMode && (
            <>
              <button 
                style={{...buttonStyle, backgroundColor: buttonSuccessColor}}
                onClick={enableMockData}
                title={t("테스트 데이터 생성")}
              >
                {t("테스트 데이터")}
              </button>
              <button 
                style={{...buttonStyle, backgroundColor: buttonDangerColor}}
                onClick={retryWithRealApi}
                title={t("실제 API로 다시 시도")}
              >
                {t("API 재시도")}
              </button>
            </>
          )}
        </div>
      )}
      
      {/* 브레드크럼 */}
      {showBreadcrumb && currentUserId && currentUserId !== user?.id && (
        <div className="navigation-breadcrumb">
          <span 
            className="breadcrumb-item root" 
            onClick={goToRootNode}
            style={{ color: breadcrumbActiveColor, cursor: 'pointer' }}
          >
            {t("내 레그")}
          </span>
          <span className="breadcrumb-separator" style={{ color: breadcrumbColor }}>›</span>
          <span className="breadcrumb-item current" style={{ color: breadcrumbColor }}>
            {unilevelTreeData?.name || `ID: ${currentUserId}`}
          </span>
        </div>
      )}
      
      {/* 조직도 컨테이너 */}
      <div 
        ref={containerRef}
        className="orgchart-container" 
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          overflow: 'hidden',
          touchAction: 'none',
          WebkitOverflowScrolling: 'touch',
          cursor: isDragging ? 'grabbing' : 'grab',
          backgroundColor: containerBackgroundColor,
        }}
      >
        {unilevelTreeLoading && <div className="loading-indicator">{t(loadingText)}</div>}
        {unilevelTreeError && (
          <div className="error-message">
            <p>{t(errorText)}</p>
            {debugMode && (
              <div className="debug-info" style={{backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '4px', marginTop: '10px', fontSize: '12px', textAlign: 'left'}}>
                <h4>{t("디버그 정보")}</h4>
                <p>{t("현재 위치")}: {window.location.href}</p>
                <p>{t("호스트")}: {window.location.hostname}</p>
                <p>{t("사용자 ID")}: {currentUserId}</p>
                {user && (
                  <div>
                    <h5>{t("로그인 사용자 정보")}</h5>
                    <p>{t("이름")}: {user.name} (ID: {user.id})</p>
                    <p>{t("회원유형")}: {user.role}</p>
                    <p>{t("추천인코드")}: {user.referralCode || t('없음')}</p>
                  </div>
                )}
                
                <h5>{t("직접 테스트하기")}</h5>
                <div style={{marginBottom: '10px'}}>
                  <label style={{display: 'block', marginBottom: '5px'}}>
                    <input 
                      type="checkbox" 
                      checked={mockingEnabled}
                      onChange={(e) => setMockingEnabled(e.target.checked)}
                    /> 
                    {t("임시 모킹 데이터 사용 (API 대신 테스트 데이터 사용)")}
                  </label>
                </div>
                
                {!mockingEnabled && (
                  <div>
                    <div style={{marginBottom: '10px'}}>
                      <label style={{display: 'block', marginBottom: '5px'}}>{t("API 호스트")}:</label>
                      <input 
                        type="text" 
                        value={customApiUrl} 
                        onChange={(e) => setCustomApiUrl(e.target.value)}
                        placeholder={t("예: https://nesem.io.kr")}
                        style={{width: '100%', padding: '5px'}}
                      />
                    </div>
                    <button 
                      onClick={retryWithRealApi}
                      style={{padding: '5px 10px', backgroundColor: primaryColor, color: 'white', border: 'none', borderRadius: '4px'}}
                    >
                      {t("API 다시 시도")}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {!unilevelTreeLoading && !unilevelTreeError && unilevelTreeData && (
          <div 
            className="orgchart"
            style={{
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoomLevel})`,
              transformOrigin: '0 0',
              transition: isDragging ? 'none' : 'transform 0.1s ease-out',
              display: 'inline-block',
              position: 'relative',
            }}
          >
            <ul className="org-tree" style={{ '--line-color': lineColor || primaryColor } as React.CSSProperties}>
              <OrganizationChartNode 
                node={mockingEnabled && currentUserId ? generateMockData(currentUserId) : unilevelTreeData} 
                onClick={handleNodeClick}
                onMoreClick={handleMoreButtonClick}
                loadingNodes={loadingNodes}
                isRootNode={true}
                lineColor={lineColor || primaryColor}
                showNodePV={showNodePV}
                showNodeRank={showNodeRank}
                showNodeGrade={showNodeGrade}
                showNodePhone={showNodePhone}
                showNodeCenter={showNodeCenter}
                showNodeGroupPV={showNodeGroupPV}
                showMoreButton={showMoreButton}
                bubbleBadgeColor={bubbleBadgeColor}
                bubbleBadgeTextColor={bubbleBadgeTextColor}
              />
            </ul>
          </div>
        )}
        {!unilevelTreeLoading && !unilevelTreeError && !unilevelTreeData && (
          <div className="empty-message">
            {t(loginRequiredText)}
            {debugMode && user && (
              <div style={{marginTop: '15px', fontSize: '12px', color: '#666'}}>
                <p>{t("사용자 정보는 있지만 유니레벨 레그 데이터를 가져오지 못했습니다.")}</p>
                <p>{t("콘솔에서 API 요청 로그를 확인하세요.")}</p>
                
                <div style={{marginTop: '15px'}}>
                  <button 
                    onClick={enableMockData}
                    style={{padding: '5px 10px', backgroundColor: secondaryColor, color: 'white', border: 'none', borderRadius: '4px', marginRight: '10px'}}
                  >
                    {t("임시 데이터로 테스트")}
                  </button>
                  <button 
                    onClick={retryWithRealApi}
                    style={{padding: '5px 10px', backgroundColor: primaryColor, color: 'white', border: 'none', borderRadius: '4px'}}
                  >
                    {t("API 다시 시도")}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BasicUnilevelLegsSkin;